// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ReactNode } from 'react';

import type { AuthUser } from '../../api/types';
import type { Lens, ViewKind } from '../../bygning/selection';
import { IconButton } from '../IconButton';
import { Kbd } from '../Kbd';
import { Logo } from '../Logo';
import { UserMenu } from '../UserMenu';
import { LensSwitch } from './LensSwitch';
import { ModeTabs, type ModeTab } from './ModeTabs';
import { ProgressRing } from './ProgressRing';
import { ViewSwitch } from './ViewSwitch';
import styles from './TopBar.module.css';

export interface TopBarProps {
  /** The breadcrumb (`Crumb`); hidden on phones. */
  crumb: ReactNode;
  tabs: readonly ModeTab[];
  currentMode: string;
  /** The lens switch, when the mode offers it (Ressourcer). */
  lens?: { value: Lens; onChange: (lens: Lens) => void } | null;
  /** The Model/Tabel/Galleri switch, when the mode offers it (Ressourcer). */
  view?: { value: ViewKind; onChange: (view: ViewKind) => void } | null;
  /** Godkendt af alle; hidden before the survey has loaded. */
  progress?: { approved: number; total: number } | null;
  /** The gear (Modelindstillinger), when the mode offers it. */
  gear?: { pressed: boolean; onToggle: () => void } | null;
  onPalette: () => void;
  /** Who is signed in (server mode); null/absent in local mode, which has no login. */
  user?: { user: AuthUser; onLogout: () => void; viaShareLink?: boolean } | null;
}

/**
 * The Bygning top bar: logo, crumb, the five mode tabs, then lens and
 * view switches, the approval ring, the gear, the ⌘K button and, at the
 * right edge, the user menu. It floats over the Ground like the tiles; on
 * phones it wraps to two rows, drops the crumb and shows the user as initials.
 */
export function TopBar({ crumb, tabs, currentMode, lens, view, progress, gear, onPalette, user }: TopBarProps) {
  return (
    <header className={styles.bar}>
      <Logo size={14} />
      <span className={styles.crumb}>{crumb}</span>
      <ModeTabs tabs={tabs} current={currentMode} />
      <div className={styles.right}>
        {lens && <LensSwitch lens={lens.value} onChange={lens.onChange} />}
        {view && <ViewSwitch view={view.value} onChange={view.onChange} />}
        {progress && <ProgressRing approved={progress.approved} total={progress.total} />}
        {gear && <GearButton pressed={gear.pressed} onToggle={gear.onToggle} />}
        <PaletteButton onClick={onPalette} />
        {user && <UserMenu user={user.user} onLogout={user.onLogout} viaShareLink={user.viaShareLink} />}
      </div>
    </header>
  );
}

/** Modelindstillinger: layers, camera views, lights, scan facts, expert pages and the pipeline. */
export function GearButton({ pressed, onToggle }: { pressed: boolean; onToggle: () => void }) {
  return (
    <IconButton label="Modelindstillinger" aria-pressed={pressed} onClick={onToggle}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M4 6h9M19 6h1M4 12h2M11 12h9M4 18h7M16 18h4" />
        <circle cx="16" cy="6" r="2.2" />
        <circle cx="8" cy="12" r="2.2" />
        <circle cx="13" cy="18" r="2.2" />
      </svg>
    </IconButton>
  );
}

/** Opens the ⌘K palette; also used by the ToolFrame's bar. */
export function PaletteButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className={styles.chip}
      onClick={onClick}
      aria-label="Søg, gå til, eksport (⌘K)"
      aria-haspopup="dialog"
    >
      <span className={styles.chipText}>Søg, gå til, eksport…</span>
      <Kbd>⌘K</Kbd>
    </button>
  );
}
